import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { complianceStatus, daysUntil, equipmentCsv, localDate, monthRange, retired, summarizeEquipment } from '../equipmentData';

const EquipmentPageContext = createContext(null);
export function EquipmentPageProvider({ children }) {
  const [range, setRange] = useState(monthRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ search: '', category: '', location: '', status: '', riskLevel: '' });
  const [metricFilter, setMetricFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const today = localDate();
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setData(null);
    setSelectedEquipment(null);
    async function load() {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/equipments/dashboard?${new URLSearchParams(range)}`, {
          signal: controller.signal, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Unable to load equipment');
        if (!controller.signal.aborted) setData(result.data);
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [range]);

  const equipment = useMemo(() => data?.equipment || [], [data]);
  const records = useMemo(() => equipment.filter(item => {
    const search = filters.search.trim().toLowerCase();
    return (!search || [item.name, item.assetId, item.serialNumber, item.brand, item.model].some(value => value?.toLowerCase().includes(search))) &&
      (!filters.category || item.category === filters.category) && (!filters.location || item.location === filters.location) &&
      (!filters.status || (retired(item) ? 'Decommissioned' : item.operationalStatus) === filters.status) &&
      (!filters.riskLevel || item.riskLevel === filters.riskLevel);
  }), [equipment, filters]);
  const filteredRecords = useMemo(() => records.filter(item => {
    switch (metricFilter) {
      case 'operational': return !retired(item) && item.operationalStatus === 'In Service';
      case 'attention': return complianceStatus(item, today) === 'Attention';
      case 'nonCompliant': return complianceStatus(item, today) === 'Non Compliant';
      case 'overdue': return !retired(item) && item.requiresRoutineMaintenance && daysUntil(item.nextMaintenanceDate, today) !== null && daysUntil(item.nextMaintenanceDate, today) < 0;
      case 'maintenance': return !retired(item) && item.requiresRoutineMaintenance && !!item.nextMaintenanceDate;
      default: return true;
    }
  }), [records, metricFilter, today]);
  const summary = useMemo(() => summarizeEquipment(records, today), [records, today]);
  const filterInventory = type => {
    setMetricFilter(type);
    document.getElementById('equipment-inventory')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const exportEquipment = () => {
    const url = URL.createObjectURL(new Blob([equipmentCsv(filteredRecords)], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `equipment-${range.startDate}-to-${range.endDate}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return <EquipmentPageContext.Provider value={{ range, setRange, data, equipment, records, filteredRecords, summary, loading, error, today,
    filters, setFilters, metricFilter, setMetricFilter, filterInventory, filtersOpen, setFiltersOpen, selectedEquipment, setSelectedEquipment, calendarOpen, setCalendarOpen, exportEquipment }}>
    {children}
  </EquipmentPageContext.Provider>;
}

export function useEquipmentPage() {
  return useContext(EquipmentPageContext);
}
